import React from 'react';
import {
  Box,
  ColumnLayout,
  KeyValuePairs,
  SpaceBetween,
  StatusIndicator,
} from '@cloudscape-design/components';
import { INPUT_STATE, describeEvidence, inputIndicator } from './checkEvidence';
import { Marked } from './placeholder';

/**
 * What one check worked from and concluded, from its stored evidence: the
 * written assessment first, then any scored criteria, then the labelled figures
 * and lists, then tables of line items. Built by describeEvidence, so the layout
 * here holds no rules about what a check means.
 */

const preWrap = { whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' };

const cell = { border: '1px solid #d5dbdb', padding: '6px 10px', verticalAlign: 'top', ...preWrap };

const Table = ({ item }) => (
  <div>
    <Box variant="awsui-key-label">{item.label}</Box>
    <div style={{ overflowX: 'auto', marginTop: '4px' }}>
      <table style={{ borderCollapse: 'collapse', width: '100%', fontSize: '13px' }}>
        <thead>
          <tr>
            {item.columns.map((c) => (
              <th
                key={c.key}
                style={{
                  ...cell, backgroundColor: '#f2f3f3', textAlign: 'left', fontWeight: 600, ...(c.format === 'money' ? { whiteSpace: 'nowrap' } : {}),
                }}
              >
                {c.title}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {item.rows.map((row, i) => (
            <tr key={i}>
              {item.columns.map((c) => (
                <td
                  key={c.key}
                  // An amount stays on one line: a narrow column must not split "$3,682.70".
                  style={c.format === 'money' ? { ...cell, textAlign: 'right', whiteSpace: 'nowrap' } : cell}
                >
                  {row[c.key]}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  </div>
);

const Criteria = ({ item }) => (
  <div>
    <Box variant="awsui-key-label">{item.label}</Box>
    <SpaceBetween size="s">
      {item.criteria.map((c) => (
        <div key={c.key} style={{ borderLeft: '3px solid #d5dbdb', paddingLeft: '12px', marginTop: '6px' }}>
          <Box variant="strong">
            {c.label}
            {c.score !== null && <Box variant="span" color="text-body-secondary">{`  ·  ${c.score} / 100`}</Box>}
          </Box>
          {c.assessment && <Box variant="p"><span style={preWrap}><Marked text={c.assessment} /></span></Box>}
          {(c.planQuote || c.planStatus) && (
            <Box variant="small" display="block" margin={{ bottom: 'xxs' }}>
              {c.planStatus && <Box variant="strong" fontSize="body-s" display="inline">{`${c.planStatus}. `}</Box>}
              {c.planQuote && <span style={{ ...preWrap, fontStyle: 'italic' }}>{`“${c.planQuote}”`}</span>}
            </Box>
          )}
          {c.recheck && (c.recheck.quote || c.recheck.status) && (
            <Box variant="small" display="block" color="text-body-secondary" margin={{ bottom: 'xxs' }}>
              {`Rechecked against the plan alone: ${c.recheck.status || 'no answer'}`}
              {c.recheck.quote && <span style={{ ...preWrap, fontStyle: 'italic' }}>{` “${c.recheck.quote}”`}</span>}
            </Box>
          )}
          {c.facts.length > 0 && (
            <Box variant="small" color="text-body-secondary">
              {c.facts.map((fact) => `${fact.label}: ${fact.text}`).join('  ·  ')}
            </Box>
          )}
        </div>
      ))}
    </SpaceBetween>
  </div>
);

const pairValue = (item) => {
  if (item.kind === 'list') {
    return (
      <ul style={{ margin: 0, paddingLeft: '18px' }}>
        {item.items.map((text, i) => <li key={i} style={preWrap}><Marked text={text} /></li>)}
      </ul>
    );
  }
  // An input's state shows as everywhere else it appears: the short colored status
  // first, then the check's own explanation on its own line when it is not a pass.
  if (item.state) {
    const explains = item.state.kind !== INPUT_STATE.OK && item.state.kind !== INPUT_STATE.NOT_REQUIRED;
    return (
      <div>
        <StatusIndicator type={inputIndicator(item.state.kind, item.state.enforced)}>{item.state.short || item.text}</StatusIndicator>
        {explains && item.text && item.text !== item.state.short && <div><span style={preWrap}><Marked text={item.text} /></span></div>}
      </div>
    );
  }
  return <span style={preWrap}><Marked text={item.text} /></span>;
};

const EvidenceView = ({ check, nameOf, omitReason }) => {
  const items = describeEvidence(check.id, check.evidence, check.details, nameOf, { omitReason });
  if (items.length === 0) return null;
  const paragraphs = items.filter((i) => i.kind === 'paragraph');
  const criteria = items.filter((i) => i.kind === 'criteria');
  const pairs = items.filter((i) => i.kind === 'value' || i.kind === 'list');
  const tables = items.filter((i) => i.kind === 'table');
  return (
    <div data-testid="isfr-check-evidence">
      <SpaceBetween size="s">
        {paragraphs.map((p, i) => (
          <div key={`p${i}`}>
            <Box variant="awsui-key-label">{p.label}</Box>
            <Box variant="p"><span style={preWrap}><Marked text={p.text} /></span></Box>
          </div>
        ))}
        {criteria.map((c, i) => <Criteria key={`c${i}`} item={c} />)}
        {pairs.length > 0 && (
          <KeyValuePairs
            columns={pairs.length > 2 ? 3 : pairs.length}
            items={pairs.map((p) => ({ label: p.label, value: pairValue(p) }))}
          />
        )}
        {tables.length > 0 && (
          <ColumnLayout columns={1}>
            {tables.map((t, i) => <Table key={`t${i}`} item={t} />)}
          </ColumnLayout>
        )}
      </SpaceBetween>
    </div>
  );
};

export default EvidenceView;
